import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixEcomOrdersBillingV1OrderBillingServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/order-billing/v1',
        destPath: '/v1/order-billing',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/order-billing',
        destPath: '/v1/order-billing',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_order-billing';

/**
 * Retrieves refund information about an order's payments, line items, shipping, and additional fees.
 *
 * The following information is returned:
 * - Whether a payment is refundable; previously refunded amounts; possible refund amounts for each payment.
 * - Refundable and previously refunded quantities for each line item, as well as shipping and additional fees.
 */
export function getOrderRefundability(
  payload: object
): RequestOptionsFactory<any> {
  function __getOrderRefundability({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order_billing',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.billing.v1.OrderBillingService.GetOrderRefundability',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersBillingV1OrderBillingServiceUrl({
        protoPath: '/v1/order-billing/get-order-refundability',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __getOrderRefundability;
}

/** Calculates refund amounts for a given list of an order's line items, shipping, and additional fees. */
export function calculateRefund(payload: object): RequestOptionsFactory<any> {
  function __calculateRefund({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order_billing',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.billing.v1.OrderBillingService.CalculateRefund',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersBillingV1OrderBillingServiceUrl({
        protoPath: '/v1/order-billing/calculate-refund',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __calculateRefund;
}

/**
 * Calls relevant payment providers and creates refund transactions for the requested payments.
 *
 * Refunds marked with `paymentRefunds.externalRefund = true` do not call payment providers and
 * only update the order's transactions records.
 */
export function refundPayments(payload: object): RequestOptionsFactory<any> {
  function __refundPayments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order_billing',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.billing.v1.OrderBillingService.RefundPayments',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersBillingV1OrderBillingServiceUrl({
        protoPath: '/v1/order-billing/refund-payments',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'refund.createdDate' },
              { path: 'orderTransactions.payments.createdDate' },
              { path: 'orderTransactions.payments.updatedDate' },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.authorizedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.captures.createdDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.void.voidedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.scheduledAction.executionDate',
              },
              { path: 'orderTransactions.refunds.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __refundPayments;
}

/**
 * Authorizes a payment using an order's saved payment method.
 *
 * Use the `delayedCaptureSettings` field to specify whether the payment should also be captured/voided immediately or at a later time.
 * Alternatively, the payment can be manually captured or voided using the Capture Authorized Payments or Void Authorized Payments methods.
 */
export function authorizeChargeWithSavedPaymentMethod(
  payload: object
): RequestOptionsFactory<any> {
  function __authorizeChargeWithSavedPaymentMethod({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order_billing',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.billing.v1.OrderBillingService.AuthorizeChargeWithSavedPaymentMethod',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersBillingV1OrderBillingServiceUrl({
        protoPath:
          '/v1/order-billing/authorize-charge-with-saved-payment-method',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'payment.createdDate' },
              { path: 'payment.updatedDate' },
              {
                path: 'payment.regularPaymentDetails.authorizationDetails.authorizedDate',
              },
              {
                path: 'payment.regularPaymentDetails.authorizationDetails.captures.createdDate',
              },
              {
                path: 'payment.regularPaymentDetails.authorizationDetails.void.voidedDate',
              },
              {
                path: 'payment.regularPaymentDetails.authorizationDetails.scheduledAction.executionDate',
              },
              { path: 'orderTransactions.payments.createdDate' },
              { path: 'orderTransactions.payments.updatedDate' },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.authorizedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.captures.createdDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.void.voidedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.scheduledAction.executionDate',
              },
              { path: 'orderTransactions.refunds.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __authorizeChargeWithSavedPaymentMethod;
}

/** Voids an order's authorized payments. */
export function voidAuthorizedPayments(
  payload: object
): RequestOptionsFactory<any> {
  function __voidAuthorizedPayments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order_billing',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.billing.v1.OrderBillingService.VoidAuthorizedPayments',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersBillingV1OrderBillingServiceUrl({
        protoPath: '/v1/order-billing/void-authorized-payments',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'orderTransactions.payments.createdDate' },
              { path: 'orderTransactions.payments.updatedDate' },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.authorizedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.captures.createdDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.void.voidedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.scheduledAction.executionDate',
              },
              { path: 'orderTransactions.refunds.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __voidAuthorizedPayments;
}

/** Captures an order's authorized payments. */
export function captureAuthorizedPayments(
  payload: object
): RequestOptionsFactory<any> {
  function __captureAuthorizedPayments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order_billing',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.billing.v1.OrderBillingService.CaptureAuthorizedPayments',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersBillingV1OrderBillingServiceUrl({
        protoPath: '/v1/order-billing/capture-authorized-payments',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'orderTransactions.payments.createdDate' },
              { path: 'orderTransactions.payments.updatedDate' },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.authorizedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.captures.createdDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.void.voidedDate',
              },
              {
                path: 'orderTransactions.payments.regularPaymentDetails.authorizationDetails.scheduledAction.executionDate',
              },
              { path: 'orderTransactions.refunds.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __captureAuthorizedPayments;
}

/** Generates receipts for an order's specified payments. */
export function generateReceipts(payload: object): RequestOptionsFactory<any> {
  function __generateReceipts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.order_billing',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.billing.v1.OrderBillingService.GenerateReceipts',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersBillingV1OrderBillingServiceUrl({
        protoPath: '/v1/order-billing/generate-receipts',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __generateReceipts;
}
